/**
 * Filters the store list in the browser. Every store is already rendered by
 * Liquid, so this is a pure show/hide over `data-store-haystack` — no geocoding
 * or API involved, which is what the design's "distance" control would imply.
 *
 * Clicking a store card swaps the map iframe's src to that store's
 * `data-store-map` embed URL, so the map re-centers and drops a pin on the
 * selected store. `aria-pressed` tracks the selected card for styling.
 */
class StoreLocator extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-store-search]');
    this.stores = Array.from(this.querySelectorAll('[data-store]'));
    this.empty = this.querySelector('[data-store-empty]');
    this.mapFrame = this.querySelector('[data-store-map-frame]');
    this.photoFrame = this.querySelector('[data-store-photo-frame]');

    this.input?.addEventListener('input', this.#onInput);
    for (const store of this.stores) {
      store.addEventListener('click', this.#onStoreClick);
      store.addEventListener('keydown', this.#onStoreKeydown);
    }
  }

  disconnectedCallback() {
    this.input?.removeEventListener('input', this.#onInput);
    for (const store of this.stores) {
      store.removeEventListener('click', this.#onStoreClick);
      store.removeEventListener('keydown', this.#onStoreKeydown);
    }
  }

  #onInput = () => {
    const query = this.input.value.trim().toLowerCase();
    let visible = 0;

    for (const store of this.stores) {
      const match = !query || (store.dataset.storeHaystack || '').includes(query);
      store.hidden = !match;
      if (match) visible++;
    }

    if (this.empty) this.empty.hidden = visible > 0;
  };

  #onStoreClick = (event) => {
    if (event.target.closest('a')) return;
    this.#selectStore(event.currentTarget);
  };

  #onStoreKeydown = (event) => {
    if (event.target.closest('a')) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.#selectStore(event.currentTarget);
  };

  #selectStore(store) {
    const mapSrc = store.dataset.storeMap;
    if (this.mapFrame && mapSrc) this.mapFrame.src = mapSrc;

    if (this.photoFrame) {
      const photoSrc = store.dataset.storePhoto;
      this.photoFrame.hidden = !photoSrc;
      if (photoSrc) {
        this.photoFrame.src = photoSrc;
        this.photoFrame.alt = store.querySelector('.store-card__name')?.textContent ?? '';
      }
    }

    for (const other of this.stores) {
      other.setAttribute('aria-pressed', String(other === store));
    }

    if (window.matchMedia('(max-width: 989px)').matches) {
      this.mapFrame?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
}

if (!customElements.get('store-locator')) {
  customElements.define('store-locator', StoreLocator);
}
